import { createAsyncThunk } from '@reduxjs/toolkit';
import { EVENTS_NAMES_MAP } from '../events-name';
import { pinterestConversionTrack } from '../utils';
import { EcEnv, INTL_CURRENCY } from '@castlery/config';
import { TRACKING_MSGS_MAP } from '../helpers';
import { logger } from '@castlery/observability';

/**
 * @description track pinterest page visit event
 * @param payload
 * @param fulfillWithValue
 * @param dispatch
 * @returns
 */
export const trackPinterestPageVisitEvent = createAsyncThunk(
  'tracking/trackPinterestPageVisitEvent',
  async (
    payload: {
      eventId: string;
      originalPrice: string;
      variant: {
        sku: string;
        name: string;
      };
    },
    { dispatch, fulfillWithValue }
  ) => {
    if (!payload.eventId || !payload.originalPrice || !payload.variant) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    try {
      const params = {
        eventName: EVENTS_NAMES_MAP.PINTEREST_CAPI_PRODUCT_PAGE_VIEW,
        eventId: payload.eventId,
        customData: {
          value: payload.originalPrice,
          content_name: payload.variant.name,
          content_ids: [payload.variant.sku],
          currency: EcEnv.NEXT_PUBLIC_CURRENCY,
        },
      };
      await dispatch(pinterestConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackPinterestPageVisitEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

/**
 * @description track pinterest add to wishlist event
 * @param payload
 * @param fulfillWithValue
 * @param dispatch
 * @returns
 */
export const trackPinterestAddToWishlistEvent = createAsyncThunk(
  'tracking/trackPinterestAddToWishlistEvent',
  async (
    payload: {
      variant: {
        sku: string;
        name: string;
        price: string;
      };
      eventId: string;
    },
    { fulfillWithValue, dispatch }
  ) => {
    if (!payload.variant || !payload.eventId) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    try {
      const params = {
        eventName: EVENTS_NAMES_MAP.PINTEREST_CAPI_CUSTOM_EVENT,
        eventId: payload.eventId,
        customData: {
          value: payload.variant.price,
          content_name: payload.variant.name,
          content_ids: [payload.variant.sku],
          currency: EcEnv.NEXT_PUBLIC_CURRENCY,
        },
      };
      await dispatch(pinterestConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackPinterestAddToWishlistEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

/**
 * @description track pinterest add to cart event
 * @param payload
 * @param fulfillWithValue
 * @param dispatch
 * @returns
 */
export const trackPinterestAddToCartEvent = createAsyncThunk(
  'tracking/trackPinterestAddToCartEvent',
  async (
    payload: { variant: { sku: string; name: string }; originalPrice: string; eventId: string },
    { fulfillWithValue, dispatch }
  ) => {
    if (!payload.variant || !payload.eventId || !payload.originalPrice) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    try {
      const params = {
        eventName: EVENTS_NAMES_MAP.PINTEREST_CAPI_ADD_TO_CART,
        eventId: payload.eventId,
        customData: {
          value: payload.originalPrice,
          content_name: payload.variant.name,
          content_ids: [payload.variant.sku],
          currency: EcEnv.NEXT_PUBLIC_CURRENCY,
        },
      };
      await dispatch(pinterestConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackPinterestAddToCartEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

/**
 * @description track pinterest add swatch to cart event
 * @param payload
 * @param fulfillWithValue
 * @param dispatch
 * @returns
 */
export const trackPinterestAddSwatchToCartEvent = createAsyncThunk(
  'tracking/trackPinterestAddSwatchToCartEvent',
  async (payload: { eventId: string; variant: { sku: string } }, { fulfillWithValue, dispatch }) => {
    if (!payload.variant) {
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
    try {
      const params = {
        eventName: EVENTS_NAMES_MAP.PINTEREST_CAPI_CUSTOM_EVENT,
        eventId: payload.eventId,
        customData: {
          content_ids: [payload.variant.sku],
        },
      };
      await dispatch(pinterestConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackPinterestAddSwatchToCartEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

export const trackPinterestSignupEvent = createAsyncThunk(
  'tracking/trackPinterestSignupEvent',
  async (payload: { eventId: string }, { fulfillWithValue, dispatch }) => {
    try {
      const params = {
        eventName: EVENTS_NAMES_MAP.PINTEREST_CAPI_SIGNUP,
        eventId: payload.eventId,
        customData: {
          value: 0.0,
          currency: INTL_CURRENCY,
          content_name: 'Sign Up',
        },
      };
      await dispatch(pinterestConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackPinterestCheckoutEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);

export const trackPinterestNewsletterSubscriptionEvent = createAsyncThunk(
  'tracking/trackPinterestNewsletterSubscriptionEvent',
  async (payload: { eventId: string }, { fulfillWithValue, dispatch }) => {
    try {
      const params = {
        eventName: EVENTS_NAMES_MAP.PINTEREST_CAPI_SIGNUP,
        eventId: payload.eventId,
        customData: {
          value: 0.0,
          currency: INTL_CURRENCY,
          content_name: 'Subscription Footer',
        },
      };
      await dispatch(pinterestConversionTrack(params));
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_SUCCESS);
    } catch (e) {
      logger.error('trackPinterestCheckoutEvent failed', { error: e });
      return fulfillWithValue(TRACKING_MSGS_MAP.TRACKING_ERROR);
    }
  }
);
